import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META } from "@/lib/constants";
import { useExecutives } from "@/context/ExecutivesContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Bell, CalendarClock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function ReminderRow({ lead, tone }) {
  const { user } = useAuth();
  const { execMap } = useExecutives();
  const managerial = user.role !== "executive";
  const color =
    tone === "overdue"
      ? "border-rose-200 bg-rose-50"
      : "border-amber-200 bg-amber-50";
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${color}`}
      data-testid={`reminder-item-${lead.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-slate-900 truncate">{lead.customer_name}</p>
        <span className="text-xs font-mono text-slate-500 whitespace-nowrap">{lead.follow_up_date}</span>
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <p className="text-xs text-slate-500 font-mono">{lead.contact_no}</p>
        {managerial && (
          <p className="text-[11px] text-slate-500">{execMap[lead.assigned_executive] || lead.assigned_executive}</p>
        )}
      </div>
    </div>
  );
}

export default function RemindersBell() {
  const [data, setData] = useState(null);
  const firstLoad = useRef(true);

  const fetchReminders = useCallback(async () => {
    try {
      const res = await api.get("/reminders");
      setData(res.data);
      if (firstLoad.current) {
        firstLoad.current = false;
        if (res.data.count > 0) {
          toast(`You have ${res.data.count} call-back${res.data.count > 1 ? "s" : ""} to follow up`, {
            description: `${res.data.due_today.length} due today · ${res.data.overdue.length} overdue`,
            icon: "🔔",
          });
        }
      }
    } catch {
      /* ignore transient errors */
    }
  }, []);

  useEffect(() => {
    fetchReminders();
    const id = setInterval(fetchReminders, 15000);
    return () => clearInterval(id);
  }, [fetchReminders]);

  const count = data?.count ?? 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="reminders-bell"
          className="relative h-9 w-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:text-[#1E3A8A] hover:border-[#1E3A8A] transition-colors"
          title="Follow-up reminders"
        >
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span
              data-testid="reminders-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="reminders-panel">
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-[#1E3A8A]" />
            Daily Reminder Digest
          </p>
          <p className="text-xs text-slate-400 mt-0.5">Call-backs due today & overdue</p>
        </div>
        <div className="max-h-[360px] overflow-y-auto p-3 space-y-3">
          {count === 0 && (
            <p className="text-sm text-slate-400 text-center py-6" data-testid="reminders-empty">
              You're all caught up — no call-backs pending.
            </p>
          )}
          {data?.overdue?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Overdue ({data.overdue.length})
              </p>
              {data.overdue.map((l) => (
                <ReminderRow key={l.id} lead={l} tone="overdue" />
              ))}
            </div>
          )}
          {data?.due_today?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-600">
                Due Today ({data.due_today.length})
              </p>
              {data.due_today.map((l) => (
                <ReminderRow key={l.id} lead={l} tone="today" />
              ))}
            </div>
          )}
          {data?.upcoming?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Upcoming ({data.upcoming.length})
              </p>
              {data.upcoming.map((l) => (
                <div key={l.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`reminder-upcoming-${l.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-slate-700 truncate">{l.customer_name}</p>
                    <span className="text-xs font-mono text-slate-400">{l.follow_up_date}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
